/**
 * Club admin console navigation + role gates (admin-console-split PR 5).
 *
 * `<club>.tktic.com/admin` is a club-management-only console: club overview
 * and brand, team/season/people setup, and the media surfaces (match
 * library + a slim club-scoped Live). The platform tooling that used to
 * squat here — Video Pipeline, Media Files, system telemetry, email/OAuth —
 * lives on the super-admin console at admin.tktic.com/super-admin.
 *
 * Sections use Lucide icons; the legacy `01`–`09` glyphs implied a sequence
 * that never existed. The overview links directly to the setup destinations.
 */

import type { LucideIcon } from "lucide-react";
import {
  CalendarRange,
  Clapperboard,
  Contact,
  Gauge,
  Palette,
  RadioTower,
  Shield,
  UsersRound,
} from "lucide-react";
import type { MePayload } from "@/lib/types";

export type AdminSectionId =
  | "overview"
  | "matches"
  | "live"
  | "people"
  | "teams"
  | "seasons"
  | "athletes"
  | "settings";

export type AdminGroupId = "club" | "setup" | "media";

export type AdminSection = {
  id: AdminSectionId;
  label: string;
  icon: LucideIcon;
  /** One-line context shown under the label. */
  kicker: string;
  group: AdminGroupId;
  /** SPA path. The hash fallback is built from this id. */
  path: string;
};

export const ADMIN_SECTIONS: AdminSection[] = [
  // Club — how the club is doing, and how it presents itself
  {
    id: "overview",
    label: "Overview",
    icon: Gauge,
    kicker: "Club health & setup",
    group: "club",
    path: "/admin/overview",
  },
  {
    id: "settings",
    label: "Profile & Brand",
    icon: Palette,
    kicker: "Identity, access, admins",
    group: "club",
    path: "/admin/settings",
  },
  // Setup — teams, the season calendar, the athlete directory, and the
  // people who get in
  {
    id: "teams",
    label: "Teams",
    icon: Shield,
    kicker: "Teams & memberships",
    group: "setup",
    path: "/admin/teams",
  },
  {
    id: "seasons",
    label: "Seasons",
    icon: CalendarRange,
    kicker: "Season calendar",
    group: "setup",
    path: "/admin/seasons",
  },
  // #908 — one record per athlete; a season team's roster is a set of
  // assignments of athletes (docs/plans/athlete-directory-season-teams-plan.md).
  {
    id: "athletes",
    label: "Athletes",
    icon: Contact,
    kicker: "Club directory",
    group: "setup",
    path: "/admin/athletes",
  },
  {
    id: "people",
    label: "People",
    icon: UsersRound,
    kicker: "Access & invites",
    group: "setup",
    path: "/admin/people",
  },
  // Media — the club's match film and live broadcasts
  {
    id: "matches",
    label: "Match Library",
    icon: Clapperboard,
    kicker: "Team videos",
    group: "media",
    path: "/admin/matches",
  },
  {
    id: "live",
    label: "Live",
    icon: RadioTower,
    kicker: "Streams & viewers",
    group: "media",
    path: "/admin/live",
  },
];

export const ADMIN_GROUPS: Array<{ id: AdminGroupId; label: string }> = [
  { id: "club", label: "Club" },
  { id: "setup", label: "Setup" },
  { id: "media", label: "Media" },
];

/** Sections only club-admin-level users see. Team admins are redirected
 * away if they hit one of these directly. */
const ADMIN_ONLY_SECTIONS = new Set<AdminSectionId>([
  "overview",
  "settings",
  "teams",
  "seasons",
  "athletes",
  "live",
]);

/** Legacy URL → current section path. Used by the router to redirect
 * bookmarked / emailed links — both the pre-fork paths and the operator
 * sections that moved to the super-admin console. */
export const LEGACY_SECTION_REDIRECTS: Record<string, string> = {
  "/admin/streams": "/admin/live",
  "/admin/system": "/admin/overview",
  "/admin/users": "/admin/people",
  // Platform tooling moved to admin.tktic.com/super-admin; a club admin
  // following an old bookmark lands on their Overview.
  "/admin/performance": "/admin/overview",
  "/admin/files": "/admin/overview",
};

/**
 * Can this user see this admin section?
 *
 * Rules:
 *   - Club-admin-level (club_admin membership or legacy global admin) sees
 *     everything.
 *   - Team admin (has any membership with `team_admin` role) sees
 *     `matches` + `people` only.
 *   - Everyone else sees nothing — the entire shell hides. (The legacy
 *     `uploader` platform token, which used to grant `matches` only, was
 *     retired in role-deprecation PR 3.)
 */
/** The /admin console is the per-CLUB admin surface in the multi-club
 * model. A "club-admin-level" caller is the new club_admin (membership on
 * the host club) OR the legacy global admin (kept working during the
 * additive rollout — see PR6 in docs/plans/multi-club-support-plan.md).
 * The platform super-admin uses /super-admin, NOT /admin. */
function isClubAdminLevel(me: MePayload | null | undefined): boolean {
  if (!me?.user) return false;
  return Boolean(me.is_club_admin) || me.user.is_global_admin;
}

export function canSeeAdminSection(
  section: AdminSectionId,
  me: MePayload | null | undefined,
): boolean {
  if (!me?.user) return false;
  if (isClubAdminLevel(me)) return true;

  // Team admin = any membership with role `team_admin`. The active
  // scope's effective_role isn't enough — a team admin scoped to a
  // viewer-role team should still see /admin/people on their admin
  // team. We check the full memberships array.
  const isTeamAdmin = me.memberships?.some((m) => m.role === "team_admin");
  if (isTeamAdmin) {
    return section === "matches" || section === "people";
  }

  // The legacy `uploader` platform token — which used to grant a
  // matches-only "Match Studio" view here — was retired in deprecation PR 3.
  // /admin access is now club_admin / team_admin (handled above); coaches use
  // the Coach workspace, not /admin.
  return false;
}

/** What's the user's best landing page inside /admin? Mirrors the
 * legacy default-landing rules. */
export function defaultAdminLanding(
  me: MePayload | null | undefined,
): AdminSectionId {
  if (!me?.user) return "matches";
  if (isClubAdminLevel(me)) return "overview";
  const isTeamAdmin = me.memberships?.some((m) => m.role === "team_admin");
  if (isTeamAdmin) return "people";
  return "matches";
}

/** True if this user can see ANY admin section. The /admin route
 * gates on this to keep plain coaches/viewers out of the shell. */
export function hasAdminAccess(me: MePayload | null | undefined): boolean {
  if (!me?.user) return false;
  if (isClubAdminLevel(me)) return true;
  if (me.memberships?.some((m) => m.role === "team_admin")) return true;
  return false;
}

/** Title chrome — "Club Admin Console" / "Team Admin Console" — driven by the
 * user's role. (The legacy `uploader` "Match Studio" title was retired in
 * deprecation PR 3.) */
export function adminConsoleTitle(me: MePayload | null | undefined): string {
  if (!me?.user) return "Admin";
  if (isClubAdminLevel(me)) return "Club Admin Console";
  if (me.memberships?.some((m) => m.role === "team_admin"))
    return "Team Admin Console";
  return "Admin";
}

export const _internal = { ADMIN_ONLY_SECTIONS };
